import { Box, createStyles, FormHelperText, InputLabel, makeStyles, TextField, Typography } from '@material-ui/core';
import cn from 'classnames';
import { useFormik } from 'formik';
import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Btn from '../../components/Btn/Btn';
import IconDialog from '../../components/IconDialog/IconDialog';
import RenderText from '../../dictionaries/RenderText';
import { useBankContext, useBrandContext } from '../../dictionaries/context/BankContext';
import useDictionary from '../../dictionaries/context/useDictionary';
import { IdentityConfirmationOption, useConfirmIdentityEmailMutation } from '../../generated/graphql';
import { getFormikValidator } from '../../utils/getFormikValidator';
import { loadingMessage } from '../../utils/loadingMessage';
import { TranquiTheme } from '../App/theme';
import AcceptTermsAndPolices from './AcceptTermsAndPolices';
import { IdentificationConfirmationStyles } from './IdentificationConfirmationStyles';
import ValidationAttemptsCounter from './components/ValidationAttemptsCounter';
import { requiredPoliciesAreAccepted } from './utils/requiredPoliciesAreAccepted';
import { useAttemptsExhaustedRedirect } from './utils/useAttemptsExhaustedRedirect';
import { useIdentityConfirmation } from './utils/useIdentityConfirmation';
import { useLegalPolicyAccepted } from './utils/useLegalAcceptance';

interface IdConfirmationEmailAddressProps {
  baseClasses: IdentificationConfirmationStyles;
  parentUrl: string;
  option: IdentityConfirmationOption;
  refetchConfirmationOptions?: () => Promise<any>;
  setShowSnackbarEmailError?: (show: boolean) => void;
}

const initialValues = {
  email: '',
  policiesAndTermsA: false,
  policiesAndTermsB: false,
};

const validator = getFormikValidator(
  { email: 'required' },
  undefined,
  undefined,
  { required: 'Este campo es obligatorio' },
);

const IdentificationConfirmationEmailAddress: React.FC<IdConfirmationEmailAddressProps> = ({
  baseClasses,
  parentUrl,
  option,
  refetchConfirmationOptions,
  setShowSnackbarEmailError,
}) => {
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const [ brand ] = useBrandContext();
  const [ bank ] = useBankContext();
  const handleBack = React.useCallback(() => history.push(parentUrl), [ history, parentUrl ]);
  const policyUpToDate = useLegalPolicyAccepted();

  // Redirigir si los intentos se agotaron
  useAttemptsExhaustedRedirect(
    option,
    parentUrl,
    setShowSnackbarEmailError,
  );
  const [ confirmIdentityEmail, { loading } ] = useIdentityConfirmation(
    useConfirmIdentityEmailMutation,
    'confirmIdentityEmail',
    parentUrl,
    {
      onError: async () => {
        if (refetchConfirmationOptions) await refetchConfirmationOptions();
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        formik.setErrors({ email: 'Email incorrecto' });
      },
    },
  );

  const { params: { email } } = useRouteMatch<{ email: string }>();
  const [ emailName, emailDomain ] = (email || '').split('@');
  const [ windowIsLoaded, setWindowsIsLoaded ] = React.useState(false);

  const formik = useFormik({
    initialValues,
    validate: validator,
    onSubmit: async ({
      email: innerEmail,
      policiesAndTermsA: termsAndConditions,
      policiesAndTermsB: dataProcessing,
    }) => {
      confirmIdentityEmail({
        variables: {
          value: innerEmail.trim(), // trim just in case
          brand: brand!,
          bank: bank!,
          termsAndConditions,
          dataProcessing,
        },
      });
    },
  });

  const emailError = formik.errors.email && formik.touched.email;
  const { gT } = useDictionary();

  React.useEffect(() => {
    if (formik.values.email?.includes('@')) {
      formik.setErrors({ email: 'No introduzcas @ ni el dominio de tu correo electrónico' });
    }
  }, [ formik, formik.values.email ]);

  React.useEffect(() => {
    if (!emailName) handleBack();
    if (windowIsLoaded || !emailName) return;
    formik.setFieldValue('email', emailName[0]);
    setWindowsIsLoaded(true);
  }, [ emailName, formik, windowIsLoaded, handleBack ]);

  // auto remove spaces
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    // eslint-disable-next-line no-param-reassign
    event.target.value = event.target.value.replace(/\s/g, '');
    formik.handleChange(event);
  };

  const disableSubmitButton = !formik.isValid
    || !requiredPoliciesAreAccepted(formik.values, policyUpToDate);

  return (
    <>
      <Typography className={cn(baseClasses.title, 'mb-2')}>
        <RenderText textKey="validation_email_title" />
      </Typography>

      <Typography className={cn(baseClasses.text, 'mt-2 mb-3')}>
        <RenderText textKey="validation_email_description" />
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <InputLabel className={classes.fieldLabel}>{gT('validation_email_label')}</InputLabel>
        <TextField
          name="email"
          data-testid="identification-confirmation-email-address__email-validation-input"
          fullWidth
          value={formik.values.email || ''}
          onChange={handleChange}
          error={!!formik.errors.email && !!formik.touched.email}
          onBlur={formik.handleBlur}
          className={cn('px-2', classes.textField, classes.borderDomain, emailError && classes.borderDomainError)}
          variant="standard"
          placeholder="micorreo"
          InputProps={{ disableUnderline: true, endAdornment: `@${emailDomain}` }}
          autoComplete="off"
        />
        {emailError && (
          <FormHelperText error className={classes.textError}>
            {formik.errors.email}
          </FormHelperText>
        )}
        <ValidationAttemptsCounter
          remainingAttempts={option?.remainingAttempts}
        />

        {!policyUpToDate ? <AcceptTermsAndPolices formik={formik} /> : null}

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Btn
            color="primary"
            disabled={disableSubmitButton}
            type="submit"
            data-testid="identification-confirmation-email-address__continue-button"
            className="ml-auto"
          >
            CONTINUAR
          </Btn>
        </Box>
      </form>

      <IconDialog
        icon="loading"
        rotating
        open={loading}
      >
        <Typography variant="h3">{loadingMessage}</Typography>
      </IconDialog>
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  textError: {
    ...theme.typography.small as any,
    color: theme.palette.salmon.main,
  },
  borderDomain: {
    border: `solid 1.5px ${theme.palette.secondary.main}`,
    borderRadius: theme.shape.borderRadius,
  },
  borderDomainError: {
    border: `solid 1.5px ${theme.palette.salmon.main}`,
  },
  fieldLabel: {
    color: theme.palette.text3.main,
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.small.fontSize,
  },
  textField: {
    padding: 1,
    color: theme.palette.black.main,
    backgroundColor: theme.palette.bg4.default,
    '&:hover fieldset': {
      borderColor: 'yellow',
    },
    '& label.Mui-focused': {
      color: theme.palette.text3.main,
    },
    '& .MuiInput-underline:after': {
      border: 'none',
    },
    '& .Mui-error': {
      borderBottom: 'none',
    },
  },
}));

export default IdentificationConfirmationEmailAddress;
